import { useEffect, useState } from 'react';

import type { Engine } from '@runtime/engine.js';

/**
 * Resolve the signed-in shared-device user's display name for the
 * status-bar SSO pill, via the agent host's optional `getCurrentUser`.
 *
 * Feature-detected and tolerant: returns undefined when there's no
 * engine (Builder preview), no host capability (browser/demo hosts), the
 * device is logged out, or the call rejects. Callers fall back to a
 * neutral label. Gated by `enabled` so we don't probe the bridge unless
 * the profile actually shows the user (ssoDisplay-entitled).
 *
 * Name preference: name → email → id.
 */
export function useCurrentUser(engine: Engine | undefined, enabled: boolean): string | undefined {
  const [name, setName] = useState<string | undefined>(undefined);

  useEffect(() => {
    const host = engine?.agentHost;
    const getCurrentUser = host?.getCurrentUser;
    if (!enabled || host === undefined || getCurrentUser === undefined) {
      setName(undefined);
      return undefined;
    }
    let cancelled = false;
    void getCurrentUser.call(host).then(
      (user) => {
        if (cancelled) return;
        setName(user ? (user.name ?? user.email ?? user.id ?? undefined) : undefined);
      },
      () => {
        if (!cancelled) setName(undefined);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [engine, enabled]);

  return name;
}
